import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { manageModels } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import { 
  BookOpen, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter, 
  Eye, 
  EyeOff,
  Calendar,
  Tag,
  User,
  X,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

interface Model {
  id: string;
  name: string;
  description: string;
  content: string;
  study_content?: string;
  tags: string[];
  category: string;
  status: 'draft' | 'published' | 'archived';
  created_by: string;
  created_at: string;
  updated_at: string;
}

const ModelManagement: React.FC = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [generatingContent, setGeneratingContent] = useState<string | null>(null);
  const { user, isAdminUser } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    study_content: '',
    tags: [] as string[],
    category: '',
    status: 'draft' as 'draft' | 'published' | 'archived'
  });

  const statusOptions = [
    { value: 'draft', label: '草稿', color: 'gray' },
    { value: 'published', label: '已发布', color: 'green' },
    { value: 'archived', label: '已归档', color: 'red' }
  ];

  useEffect(() => {
    if (isAdminUser) {
      fetchModels();
    }
  }, [isAdminUser]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await manageModels('list', {
        status: statusFilter === 'all' ? undefined : statusFilter,
        category: categoryFilter || undefined,
        search: searchTerm || undefined
      });
      
      if (response.success) {
        setModels((response.data as any)?.models || []);
      } else {
        throw new Error(response.error || '获取模型失败');
      }
    } catch (error) {
      console.error('获取模型失败:', error);
      toast.error('获取模型失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await manageModels('create', {
        name: formData.name,
        description: formData.description,
        model_data: { content: formData.content },
        study_content: formData.study_content,
        tags: formData.tags,
        category: formData.category,
        status: formData.status
      });

      if (response.success) {
        toast.success('模型创建成功');
        setShowCreateModal(false);
        resetForm();
        fetchModels();
      } else {
        throw new Error(response.error || '创建失败');
      }
    } catch (error) {
      console.error('创建模型失败:', error);
      toast.error('创建模型失败');
    }
  };

  const handleUpdate = async () => {
    if (!selectedModel) return;

    try {
      const response = await manageModels('update', {
        model_id: selectedModel.id,
        name: formData.name,
        description: formData.description,
        model_data: { content: formData.content },
        study_content: formData.study_content,
        tags: formData.tags,
        category: formData.category,
        status: formData.status
      });

      if (response.success) {
        toast.success('模型更新成功');
        setShowEditModal(false);
        setSelectedModel(null);
        resetForm();
        fetchModels();
      } else {
        throw new Error(response.error || '更新失败');
      }
    } catch (error) {
      console.error('更新模型失败:', error);
      toast.error('更新模型失败');
    }
  };

  const handleDelete = async (model: Model) => {
    if (!confirm(`确定要删除模型「${model.name}」吗？`)) return;

    try {
      const response = await manageModels('delete', { model_id: model.id });
      
      if (response.success) {
        toast.success('模型删除成功');
        fetchModels();
      } else {
        throw new Error(response.error || '删除失败');
      }
    } catch (error) {
      console.error('删除模型失败:', error);
      toast.error('删除模型失败');
    }
  };

  const handleStatusChange = async (model: Model, newStatus: 'draft' | 'published' | 'archived') => {
    try {
      const response = await manageModels('update', {
        model_id: model.id,
        status: newStatus
      });

      if (response.success) {
        toast.success(`模型状态已更新为${statusOptions.find(s => s.value === newStatus)?.label}`);
        fetchModels();
      } else {
        throw new Error(response.error || '状态更新失败');
      }
    } catch (error) {
      console.error('状态更新失败:', error);
      toast.error('状态更新失败');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      content: '',
      study_content: '',
      tags: [],
      category: '',
      status: 'draft'
    });
  };

  const openEditModal = (model: Model) => {
    setSelectedModel(model);
    setFormData({
      name: model.name,
      description: model.description,
      content: model.content,
      study_content: model.study_content || '',
      tags: model.tags,
      category: model.category,
      status: model.status
    });
    setShowEditModal(true);
  };

  const openDetailModal = (model: Model) => {
    setSelectedModel(model);
    setShowDetailModal(true);
  };

  const openPreviewModal = (model: Model) => {
    setSelectedModel(model);
    setShowPreviewModal(true);
  };

  const filteredModels = models.filter(model => {
    const matchesSearch = model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         model.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || model.status === statusFilter;
    const matchesCategory = !categoryFilter || model.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const getStatusInfo = (status: string) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  const generateStudyContent = async (model: Model) => {
    if (!confirm(`确定要为模型「${model.name}」生成深度学习内容吗？这将调用AI服务生成适合用户查看的学习资料。`)) {
      return;
    }

    try {
      setGeneratingContent(model.id);
      
      // 获取当前用户的 session token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        throw new Error('认证失败，请重新登录');
      }
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/aimodule-content-admin-generate-edu?type=study_content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          model_id: model.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || '生成失败');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success('学习内容生成成功！');
        fetchModels(); // 刷新列表
      } else {
        throw new Error(result.error || '生成失败');
      }
    } catch (error) {
      console.error('生成学习内容失败:', error);
      toast.error('生成学习内容失败: ' + (error as Error).message);
    } finally {
      setGeneratingContent(null);
    }
  };

  // 权限检查
  if (!isAdminUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">访问受限</h3>
          <p className="text-gray-600">只有管理员可以访问模型管理功能</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面头部 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <BookOpen className="w-8 h-8 mr-3 text-blue-600" />
                模型管理
              </h1>
              <p className="text-lg text-gray-600 mt-2">
                管理思维模型的创建、编辑和发布
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              创建模型
            </button>
          </div>
        </div>

        {/* 搜索和筛选 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="搜索模型名称或描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">所有状态</option>
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="分类筛选"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={fetchModels}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                刷新
              </button>
            </div>
          </div>
        </div>

        {/* 模型列表 */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    模型信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    分类/标签
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    学习内容
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredModels.map((model) => {
                  const statusInfo = getStatusInfo(model.status);
                  
                  return (
                    <tr key={model.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {model.name}
                          </div>
                          <div className="text-sm text-gray-500 line-clamp-2">
                            {model.description}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={model.status}
                          onChange={(e) => handleStatusChange(model, e.target.value as any)}
                          className={`text-sm rounded-full px-3 py-1 font-medium border-0 focus:ring-2 focus:ring-blue-500 bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}
                        >
                          {statusOptions.map(status => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          {model.category && (
                            <div className="text-sm text-gray-900 mb-1">
                              {model.category}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-1">
                            {model.tags.slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                            {model.tags.length > 3 && (
                              <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                                +{model.tags.length - 3}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {model.study_content ? (
                            <span className="inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                              已生成
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                              <span className="w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                              未生成
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(model.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openDetailModal(model)}
                            className="text-gray-600 hover:text-gray-900"
                            title="查看详情"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openPreviewModal(model)}
                            className="text-blue-600 hover:text-blue-900"
                            title="预览页面效果"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => generateStudyContent(model)}
                            disabled={generatingContent === model.id}
                            className="text-purple-600 hover:text-purple-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={model.study_content ? "重新生成学习内容" : "生成学习内容"}
                          >
                            <Sparkles className={`w-4 h-4 ${generatingContent === model.id ? 'animate-spin' : ''}`} />
                          </button>
                          <button
                            onClick={() => openEditModal(model)}
                            className="text-blue-600 hover:text-blue-900"
                            title="编辑"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(model)}
                            className="text-red-600 hover:text-red-900"
                            title="删除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredModels.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                没有找到匹配的模型
              </h3>
              <p className="text-gray-600">
                尝试调整搜索条件或创建新的模型
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 创建模型模态框 */}
      {showCreateModal && (
        <ModelFormModal
          title="创建模型"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleCreate}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
        />
      )}

      {/* 编辑模型模态框 */}
      {showEditModal && (
        <ModelFormModal
          title="编辑模型"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleUpdate}
          onClose={() => {
            setShowEditModal(false);
            setSelectedModel(null);
            resetForm();
          }}
        />
      )}

      {/* 模型详情模态框 */}
      {showDetailModal && selectedModel && (
        <ModelDetailModal
          model={selectedModel}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedModel(null);
          }}
        />
      )}

      {/* 模型预览模态框 */}
      {showPreviewModal && selectedModel && (
        <ModelPreviewModal
          model={selectedModel}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedModel(null);
          }}
        />
      )}
    </div>
  );
};

// 模型表单模态框组件
interface ModelFormModalProps {
  title: string;
  formData: any;
  setFormData: (data: any) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const ModelFormModal: React.FC<ModelFormModalProps> = ({
  title,
  formData,
  setFormData,
  onSubmit,
  onClose
}) => {
  const [tagInput, setTagInput] = useState('');
  const [activeTab, setActiveTab] = useState<'basic' | 'content' | 'study'>('basic');

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag: string) => tag !== tagToRemove)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        
        {/* 标签页导航 */}
        <div className="flex border-b mb-6">
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-4 py-2 text-sm font-medium border-b-2 mr-4 ${
              activeTab === 'basic'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            基本信息
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`px-4 py-2 text-sm font-medium border-b-2 mr-4 ${
              activeTab === 'content'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            模型内容
          </button>
          <button
            onClick={() => setActiveTab('study')}
            className={`px-4 py-2 text-sm font-medium border-b-2 mr-4 ${
              activeTab === 'study'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            学习资料
          </button>
        </div>
        
        {/* 标签页内容 */}
        <div className="min-h-[400px]">
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    模型名称 *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入模型名称"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    模型描述
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="请输入模型描述"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    分类
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入分类"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    状态
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="draft">草稿</option>
                    <option value="published">已发布</option>
                    <option value="archived">已归档</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    标签
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag()}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="输入标签后按回车添加"
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      添加
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag: string, index: number) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'content' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                模型内容 *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                className="w-full h-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                placeholder="请输入模型内容（支持 Markdown 格式）"
              />
              <p className="text-xs text-gray-500 mt-1">
                支持 Markdown 格式，可以使用标题、列表、链接等格式
              </p>
            </div>
          )}

          {activeTab === 'study' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                学习资料 (HTML格式)
              </label>
              <textarea
                value={formData.study_content}
                onChange={(e) => setFormData({...formData, study_content: e.target.value})}
                className="w-full h-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                placeholder="请输入学习资料的HTML内容，或点击生成按钮让AI自动生成"
              />
              <p className="text-xs text-gray-500 mt-1">
                支持HTML格式。建议使用AI生成功能，会基于模型内容生成结构化的学习资料。
              </p>
              {formData.study_content && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">预览效果：</h4>
                  <div 
                    className="border border-gray-200 rounded-lg p-4 max-h-48 overflow-y-auto prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-h1:text-lg prose-h2:text-base prose-h3:text-sm"
                    dangerouslySetInnerHTML={{ __html: formData.study_content }}
                    style={{ 
                      fontSize: '14px',
                      lineHeight: '1.6'
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            取消
          </button>
          <button
            onClick={onSubmit}
            disabled={!formData.name.trim() || !formData.content.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};

// 模型详情模态框组件
interface ModelDetailModalProps {
  model: Model;
  onClose: () => void;
}

const ModelDetailModal: React.FC<ModelDetailModalProps> = ({ model, onClose }) => {
  const [activeTab, setActiveTab] = useState<'content' | 'study'>('content');
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{model.name}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* 标签页 */}
        <div className="flex border-b mb-4">
          <button
            onClick={() => setActiveTab('content')}
            className={`px-4 py-2 text-sm font-medium border-b-2 mr-4 ${
              activeTab === 'content'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            模型内容
          </button>
          <button
            onClick={() => setActiveTab('study')}
            className={`px-4 py-2 text-sm font-medium border-b-2 mr-4 ${
              activeTab === 'study'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            学习资料 {model.study_content && <span className="text-green-600">✓</span>}
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧：元信息 */}
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">基本信息</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-500">状态：</span>
                  <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                    model.status === 'published' ? 'bg-green-100 text-green-800' :
                    model.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {model.status === 'published' ? '已发布' : 
                     model.status === 'draft' ? '草稿' : '已归档'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">分类：</span>
                  <span className="ml-1">{model.category || '未分类'}</span>
                </div>
                <div>
                  <span className="text-gray-500">创建时间：</span>
                  <span className="ml-1">{new Date(model.created_at).toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-500">更新时间：</span>
                  <span className="ml-1">{new Date(model.updated_at).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">描述</h4>
              <p className="text-sm text-gray-600">{model.description || '暂无描述'}</p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">标签</h4>
              <div className="flex flex-wrap gap-1">
                {model.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
                {model.tags.length === 0 && (
                  <span className="text-sm text-gray-500">暂无标签</span>
                )}
              </div>
            </div>
          </div>

          {/* 右侧：内容预览 */}
          <div className="lg:col-span-2">
            {activeTab === 'content' ? (
              <>
                <h4 className="text-sm font-medium text-gray-700 mb-2">模型内容预览</h4>
                <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                  <div className="prose max-w-none">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-4 last:mb-0 text-gray-700 leading-relaxed">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700">{children}</ol>,
                        li: ({ children }) => <li className="text-gray-700">{children}</li>,
                        h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-gray-900">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-xl font-bold mb-3 text-gray-900">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-lg font-bold mb-2 text-gray-900">{children}</h3>,
                        strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                        em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
                        code: ({ children }) => <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">{children}</code>,
                        pre: ({ children }) => <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto mb-4">{children}</pre>,
                        blockquote: ({ children }) => <blockquote className="border-l-4 border-blue-500 pl-4 italic mb-4 text-gray-600">{children}</blockquote>,
                      }}
                    >
                      {model.content}
                    </ReactMarkdown>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h4 className="text-sm font-medium text-gray-700 mb-2">学习资料预览</h4>
                <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                  {model.study_content ? (
                    <>
                      {/* 调试信息 - 可选显示 */}
                      {process.env.NODE_ENV === 'development' && (
                        <details className="mb-4 text-xs text-gray-500">
                          <summary>调试信息</summary>
                          <div className="mt-2 bg-gray-50 p-2 rounded text-xs font-mono">
                            内容长度: {model.study_content.length}<br/>
                            内容类型: {typeof model.study_content}<br/>
                            内容预览: {model.study_content.substring(0, 100)}...
                          </div>
                        </details>
                      )}
                      <div 
                        className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-h1:text-lg prose-h2:text-base prose-h3:text-sm"
                        dangerouslySetInnerHTML={{ __html: model.study_content }}
                        style={{ 
                          fontSize: '14px',
                          lineHeight: '1.6'
                        }}
                      />
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500 mb-4">暂无学习资料</p>
                      <p className="text-sm text-gray-400">
                        点击表格中的 ✨ 按钮生成AI深度学习内容
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// 模型预览模态框组件 - 复用首页样式
interface ModelPreviewModalProps {
  model: Model;
  onClose: () => void;
}

const ModelPreviewModal: React.FC<ModelPreviewModalProps> = ({ model, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-50 rounded-lg w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* 模态框头部 */}
        <div className="bg-white border-b p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center">
            <ExternalLink className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">页面预览 - {model.name}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 预览内容 - 复用首页样式 */}
        <div className="flex-1 overflow-y-auto">
          {/* 顶部导航栏 - 简化版 */}
          <header className="bg-white shadow-sm border-b sticky top-0 z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">L</span>
                  </div>
                  <span className="text-xl font-bold text-gray-900">LeapWard</span>
                </div>
                <div className="text-sm text-gray-500">预览模式</div>
              </div>
            </div>
          </header>

          {/* 主内容区域 */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* 面包屑导航 */}
            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
              <span className="hover:text-blue-600 transition-colors">首页</span>
              <span className="text-gray-400">›</span>
              <span className="hover:text-blue-600 transition-colors">模型工厂</span>
              <span className="text-gray-400">›</span>
              <span className="text-gray-900 font-medium truncate">{model.name}</span>
            </nav>

            {/* Header */}
            <div className="mb-6">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-xl font-bold text-gray-900 mb-1">
                      {model.name}
                    </h1>
                    <p className="text-sm text-gray-600 mb-2">{model.description}</p>
                    {model.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {model.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
                </div>
              </div>
            </div>

            {/* Model Content - 与首页完全一致的样式 */}
            <div className="bg-white rounded-lg shadow-sm mb-6">
              <div className="p-6">
                <div className="prose max-w-none">
                  <div 
                    className="text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ 
                      __html: (model.content || '')
                        .replace(/^```html\s*/i, '')
                        .replace(/\s*```\s*$/, '')
                        .trim()
                    }}
                  />
                </div>
              </div>
            </div>

            {/* 功能提示区域 - 模拟首页效果 */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 mb-6">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="w-5 h-5 mr-2 text-blue-600">💬</span>
                  解锁更多功能
                </h3>
                <p className="text-gray-600 mb-4">
                  登录后您可以享受更多功能：AI智能讨论、记录学习笔记、个性化学习路径等
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    立即登录
                  </button>
                  <button className="inline-flex items-center justify-center px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
                    免费注册
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 底部提示 */}
        <div className="bg-white border-t p-3 flex-shrink-0">
          <div className="text-center text-sm text-gray-500">
            🔍 这是页面预览效果，与用户看到的首页样式完全一致
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelManagement;